/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import './mobile.scss';
import { useAuth } from '../../../Hooks/useAuth';
import downArrowIcon from '../../../Assets/images/svg/dar_arrow.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../Contexts/AuthContext';
import mobileLogout from '../../../Assets/images/svg/mobile_logout.svg';
import { ClickAwayListener } from '@mui/material';
import PropTypes from 'prop-types';
import { getActiveIdsFromPath } from '../activeRouteUtils';

const getNameInitials = (name) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase();
};

const MobileSidebar = ({ isOpen, onToggle, menuItems }) => {
  const { logout } = useAuth();
  const { userProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const handleProfileClick = () => {
    onToggle();
    navigate('/employee-profile', { state: { emailId: user.mailId } });
  };

  const user = useMemo(() => ({
    profilePic: userProfile.picture,
    name: userProfile.user_name,
    mailId: userProfile.email,
  }),
  [userProfile],
  );

  const nameInitials = useMemo(() => getNameInitials(user.name), [user.name]);
  const getNestedSubmenuItems = useCallback(
    (submenuItem) => (
      submenuItem.submenuOptions?.length
        ? submenuItem.submenuOptions
        : (submenuItem.submenuItems || [])
    ),
    [],
  );
  // active* reflects the CURRENT ROUTE ONLY - set from the location.pathname effect below,
  // never from a click handler - so highlighting matches where the user is, not what they're
  // peeking into.
  const [activeMenuItem, setActiveMenuItem] = useState(
    () => Number.parseInt(localStorage.getItem('activeMenuItem'), 10) || null,
  );
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(
    () => Number.parseInt(localStorage.getItem('activeSubmenuItem'), 10) || null,
  );
  const [activeSubmenuOption, setActiveSubmenuOption] = useState(
    () => Number.parseInt(localStorage.getItem('activeSubmenuOption'), 10) || null,
  );

  // expanded* only controls whether a dropdown is OPEN, toggled purely by clicks.
  const [expandedMenuItem, setExpandedMenuItem] = useState(
    () => Number.parseInt(localStorage.getItem('activeMenuItem'), 10) || null,
  );
  const [expandedSubmenuItem, setExpandedSubmenuItem] = useState(
    () => Number.parseInt(localStorage.getItem('activeSubmenuItem'), 10) || null,
  );

  useEffect(() => {
    if (location.pathname === '/employee-profile') {
      setActiveMenuItem(null);
      setActiveSubmenuItem(null);
      setActiveSubmenuOption(null);
      setExpandedMenuItem(null);
      setExpandedSubmenuItem(null);
      localStorage.removeItem('activeMenuItem');
      localStorage.removeItem('activeSubmenuItem');
      localStorage.removeItem('activeSubmenuOption');
      return;
    }

    const activeIds = getActiveIdsFromPath(menuItems, location.pathname);
    if (!activeIds) return;

    setActiveMenuItem(activeIds.activeMenuItem);
    setActiveSubmenuItem(activeIds.activeSubmenuItem);
    setActiveSubmenuOption(activeIds.activeSubmenuOption);
    setExpandedMenuItem(activeIds.activeMenuItem);
    setExpandedSubmenuItem(activeIds.activeSubmenuItem);
    localStorage.setItem('activeMenuItem', activeIds.activeMenuItem ?? '');
    localStorage.setItem('activeSubmenuItem', activeIds.activeSubmenuItem ?? '');
    localStorage.setItem('activeSubmenuOption', activeIds.activeSubmenuOption ?? '');
  }, [location.pathname, menuItems]);

  useEffect(() => {
    if (!isOpen || !sidebarRef.current) return;

    const scrollTarget = (
      sidebarRef.current.querySelector('.submenu-options li.active .sub-menu-item-options')
      || sidebarRef.current.querySelector('.submenu li.active .sub-menu-item')
      || sidebarRef.current.querySelector('.menu-item.active')
    );

    if (!scrollTarget) return;

    requestAnimationFrame(() => {
      scrollTarget.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
  }, [isOpen, activeMenuItem, activeSubmenuItem, activeSubmenuOption, location.pathname]);

  const toggleMenu = useCallback(
    (id, link = '') => {
      if (link) {
        navigate(link);
        onToggle();
        return;
      }

      // No link - this click only opens/closes the dropdown, it must never
      // mark the module as active/highlighted since the route hasn't changed.
      const newExpandedMenuItem = expandedMenuItem === id ? null : id;
      const activeIdsFromPath = getActiveIdsFromPath(menuItems, location.pathname);
      const shouldRestoreFromPath = activeIdsFromPath
        && activeIdsFromPath.activeMenuItem === id;

      setExpandedMenuItem(newExpandedMenuItem);
      setExpandedSubmenuItem(
        shouldRestoreFromPath ? activeIdsFromPath.activeSubmenuItem : null,
      );
    },
    [expandedMenuItem, navigate, onToggle, menuItems, location.pathname],
  );

  const toggleSubmenu = useCallback(
    (id, link = '', hasNestedSubmenuItems = false) => {
      if (hasNestedSubmenuItems) {
        // Just opening nested options - expanded state only, active stays put.
        const newExpandedSubmenuItem = expandedSubmenuItem === id ? null : id;
        setExpandedSubmenuItem(newExpandedSubmenuItem);
        return;
      }
      if (link) {
        navigate(link);
        onToggle();
      }
    },
    [expandedSubmenuItem, navigate, onToggle],
  );

  const toggleSubmenuOptions = useCallback(
    (id, link) => {
      // Leaf node - always navigates. Active state comes from the route effect.
      if (link) {
        navigate(link);
        onToggle();
      }
    },
    [navigate, onToggle],
  );

  const toggleMasterSubmenu = useCallback(
    (id) => {
      // Arrow-only click to expand/collapse - never touches active/highlight state.
      const newExpandedMenuItem = id === expandedMenuItem ? null : id;
      const activeIdsFromPath = getActiveIdsFromPath(menuItems, location.pathname);
      const shouldRestoreFromPath = activeIdsFromPath
        && activeIdsFromPath.activeMenuItem === id;

      setExpandedMenuItem(newExpandedMenuItem);
      setExpandedSubmenuItem(
        shouldRestoreFromPath ? activeIdsFromPath.activeSubmenuItem : null,
      );
    },
    [expandedMenuItem, menuItems, location.pathname],
  );

  const handleLinkClick = (e, callback) => {
    // Allow opening in new tab with Ctrl/Cmd+click or middle-click
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return; // Let the browser handle it
    }
    // Prevent default navigation and use custom handler
    e.preventDefault();
    callback();
  };
  const renderNestedSubmenuItem = (nestedSubmenuItem, isActiveNestedSubmenuItem) => {
    return (
      <li
        key={nestedSubmenuItem.id}
        className={isActiveNestedSubmenuItem ? 'active' : ''}
      >
        <a
          href={nestedSubmenuItem.link || '#'}
          className="sub-menu-item-options"
          onClick={(e) => handleLinkClick(
            e,
            () => toggleSubmenuOptions(nestedSubmenuItem.id, nestedSubmenuItem.link),
          )}
          onAuxClick={(e) => e.button === 1 && e.preventDefault()}
        >

          <span className="sub-options-class">{nestedSubmenuItem.text}</span>
        </a>
      </li>
    );
  };

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => isOpen && onToggle()}
    >
      <div ref={sidebarRef} className={`${isOpen ? 'mobile-view-sidebar' : 'hide-sidebar'}`}>
        <div className="user-profile-div">
          <button
            className="avatar"
            style={{ opacity: hovered ? 0.7 : 1 }}
            onClick={handleProfileClick}
          >
            <div className="avatar-circle">
              {user.profilePic ? (
                <img
                  src={user.profilePic}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                />
              ) : (
                nameInitials
              )}
            </div>
          </button>
          <div className="profile-info">
            <button
              className="name"
              onTouchStart={() => setHovered(true)}
              onTouchEnd={() => setHovered(false)}
              onClick={handleProfileClick}
            >
              {user.name}
            </button>
            <div className="mail-id">{user.mailId}</div>
          </div>
        </div>
        <ul className="menu-list">
          {menuItems.map((item) => {
            const isActiveMenuItem = activeMenuItem === item.id;
            const hasSubmenu = item.submenuItems.length > 0;
            const isSubmenuOpen = expandedMenuItem === item.id && isOpen;
            const menuItemTypeClass = hasSubmenu ? 'has-submenu' : 'o-submenu';
            return (
              <li
                key={item.id}
                className={`${isActiveMenuItem ? 'active' : ''} ${menuItemTypeClass}`}
              >
                <a
                  href={item.link || '#'}
                  className={`menu-item ${isActiveMenuItem ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick(
                    e,
                    () => toggleMenu(item.id, item?.link),
                  )}
                  onAuxClick={(e) => e.button === 1 && e.preventDefault()}
                >
                  <img
                    src={isActiveMenuItem ? item.selectedIcon : item.icon}
                    alt={`${item.text}`}
                    className={`menu-icon ${isActiveMenuItem ? 'active' : ''}`}
                  />
                  {isOpen && <span className="custom-class">{item.text}</span>}
                  {isOpen && hasSubmenu && (
                    <button
                      className="down-arrow"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleMasterSubmenu(item.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.stopPropagation();
                          toggleMasterSubmenu(item.id);
                        }
                      }}
                    >
                      <img
                        className={isSubmenuOpen ? 'rotated' : ''}
                        src={downArrowIcon}
                        alt="Down Arrow"
                      />
                    </button>
                  )}
                </a>
                {hasSubmenu && (
                  <ul className={`submenu ${isSubmenuOpen ? 'open' : ''}`}>
                    {item.submenuItems.map((submenuItem) => {
                      const isActiveSubmenuItem
                      = activeSubmenuItem === submenuItem.id;
                      const nestedSubmenuItems = getNestedSubmenuItems(submenuItem);
                      const hasNestedSubmenuItems = nestedSubmenuItems.length > 0;
                      const useRevenueStyles = Boolean(submenuItem.useRevenueStyles);
                      const isSubmenuOptionsOpen = expandedSubmenuItem === submenuItem.id && isSubmenuOpen;
                      const submenuItemTypeClass = hasNestedSubmenuItems ? 'has-submenu' : 'no-submenu';
                      const submenuItemStateClass = (
                        isActiveSubmenuItem && hasNestedSubmenuItems
                          ? 'active-submenu-options'
                          : ''
                      );
                      return (
                        <li
                          key={submenuItem.id}
                          className={[
                            isActiveSubmenuItem ? 'active' : '',
                            submenuItemTypeClass,
                            submenuItemStateClass,
                          ].join(' ')}
                        >
                          <a
                            href={submenuItem.link || '#'}
                            className="sub-menu-item"
                            onClick={(e) => handleLinkClick(
                              e,
                              () => toggleSubmenu(
                                submenuItem.id,
                                submenuItem.link,
                                hasNestedSubmenuItems,
                              ),
                            )}
                            onAuxClick={(e) => e.button === 1 && e.preventDefault()}
                          >

                            <span className={
                              useRevenueStyles
                                ? 'sub-options-revenue'
                                : 'sub-class'
                            }
                            >
                              {submenuItem.text}
                            </span>
                            {hasNestedSubmenuItems && (
                              <img
                                className={`submenu-option-down-arrow ${isSubmenuOptionsOpen ? 'rotated' : ''}`}
                                src={downArrowIcon}
                                alt="Down Arrow"
                              />
                            )}
                          </a>
                          {hasNestedSubmenuItems && (
                            <ul className={`submenu-options ${isSubmenuOptionsOpen ? 'open' : ''}`}>
                              {nestedSubmenuItems.map((nestedSubmenuItem) => {
                                const isActiveNestedSubmenuItem = activeSubmenuOption === nestedSubmenuItem.id;
                                return renderNestedSubmenuItem(nestedSubmenuItem, isActiveNestedSubmenuItem);
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
        <div className="log-out">
          <button id="logout-icon" className="logout-button" onClick={logout}>
            <img src={mobileLogout} alt="Logout" />
          </button>
          <button id="logout-text" className="logout-text" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    </ClickAwayListener>
  );
};

MobileSidebar.propTypes = {
  menuItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default MobileSidebar;
